import { useEffect, useRef, useState } from "react";
import { Play, Pause } from "lucide-react";

function fmt(s) {
  if (!s && s !== 0) return "00:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}

// Audio player with a static waveform that fills as playback progresses.
export default function VoicePlayer({ src, duration }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [total, setTotal] = useState(duration || 0);

  const bars = useRef(
    Array.from({ length: 48 }, () => 0.25 + Math.random() * 0.75)
  ).current;

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => setCurrent(a.currentTime);
    const onMeta = () => { if (a.duration && isFinite(a.duration)) setTotal(a.duration); };
    const onEnd = () => { setPlaying(false); setCurrent(0); };
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("ended", onEnd);
    return () => {
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("ended", onEnd);
    };
  }, [src]);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else { a.play(); setPlaying(true); }
  };

  const progress = total ? current / total : 0;

  return (
    <div data-testid="voice-player" className="bg-ink text-white p-5 space-y-4 border border-neutral-800">
      <audio ref={audioRef} src={src} preload="metadata" />
      <div className="flex items-center gap-4">
        <button
          data-testid="voice-play-toggle"
          onClick={toggle}
          className="w-12 h-12 shrink-0 rounded-full bg-white text-ink flex items-center justify-center hover:bg-rust hover:text-white transition-colors"
          aria-label={playing ? "Pause" : "Play"}
        >
          {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
        </button>
        <div className="flex-1 flex items-center gap-[2px] h-12">
          {bars.map((h, i) => {
            const active = i / bars.length <= progress;
            return (
              <span
                key={i}
                className="flex-1 rounded-full transition-colors"
                style={{ height: `${h * 100}%`, background: active ? "#B84A2A" : "#3a3a3a" }}
              />
            );
          })}
        </div>
      </div>
      <div className="flex justify-between font-mono text-xs text-neutral-400">
        <span>{fmt(current)}</span>
        <span>{fmt(total)}</span>
      </div>
    </div>
  );
}

export { fmt };
